// Window typings for the HubSpot Conversations global API live in hubSpot.d.ts (ambient).

// The identity (email) the chat widget was last loaded with. Tracked at module scope because
// the widget is a page-global singleton: it lets us re-create the widget only when the user
// actually changes, instead of on every panel open.
let loadedIdentificationEmail: string | undefined;

/**
 * Loads / re-loads the chat widget for the current identified user. `hsConversationsSettings`
 * (including identificationEmail/Token) must already be set when this is called.
 *
 * - First load this session: the SDK auto-loads from hsConversationsSettings (loadImmediately).
 * - Different user than the widget currently holds: re-create it via clear({ resetWidget: true })
 *   so HubSpot re-reads the new identification token. `widget.load()` alone does NOT switch the
 *   identified visitor, so the new user's conversation would otherwise never load.
 * - Same user re-opening the panel: re-render into the fresh inline container, preserving state.
 */
export function loadHubSpotChat(scriptAlreadyLoaded: boolean, identificationEmail: string): void {
  if (!scriptAlreadyLoaded) {
    loadedIdentificationEmail = identificationEmail;

    return;
  }

  if (loadedIdentificationEmail !== identificationEmail) {
    loadedIdentificationEmail = identificationEmail;
    window.HubSpotConversations?.clear?.({ resetWidget: true });

    return;
  }

  window.HubSpotConversations?.widget?.remove();
  window.HubSpotConversations?.widget?.load();
}

/**
 * Marks the chat for re-identification on its next load (login, logout, switch tenant / org unit).
 *
 * It deliberately does NOT clear the widget itself: the teardown + re-create happens once, in
 * loadHubSpotChat, which runs with the new user's token already set. Clearing here too would fire
 * a second clear({ resetWidget: true }) milliseconds earlier with no identity — and that rapid
 * double clear/re-create leaves the widget unidentified (the returning user's conversation never
 * loads until a full page refresh).
 */
export function resetHubSpotChat(): void {
  // Forget the previous user so the next load re-creates the widget with the new identity.
  loadedIdentificationEmail = undefined;

  if (window.hsConversationsSettings) {
    delete window.hsConversationsSettings.identificationEmail;
    delete window.hsConversationsSettings.identificationToken;
  }
}
